import { Palette } from "@mui/material";
import { palette } from "theme";
import { AssessmentLevelInput } from "../types";

export const MIN_ASSESSMENT_LEVELS = 2;
export const MAX_ASSESSMENT_LEVELS = 10;

const DEFAULT_LEVELS = [
  { from: 0, to: 39, label: "Junior" },
  { from: 40, to: 74, label: "Middle" },
  { from: 75, to: 100, label: "Senior" },
];

export function getDefaultAssessmentLevels(): AssessmentLevelInput[] {
  return DEFAULT_LEVELS.map((level, index) => ({
    ...level,
    color: palette.grade[index as keyof Palette["grade"]],
  }));
}

export function normalizeAssessmentLevels(
  levels?: Array<
    Pick<AssessmentLevelInput, "from" | "to" | "label"> & Partial<Pick<AssessmentLevelInput, "color">>
  > | null
): AssessmentLevelInput[] {
  if (!levels?.length) return getDefaultAssessmentLevels();

  return levels.map((level, index) => ({
    from: index === 0 ? 0 : Math.max(0, Number(level.from)),
    to: Math.max(0, Number(level.to)),
    label: level.label || "",
    color: level.color || palette.grade[index as keyof Palette["grade"]],
  }));
}

export function syncAssessmentLevels(levels: AssessmentLevelInput[]): AssessmentLevelInput[] {
  return levels.map((level, index, list) => {
    const from = index === 0 ? 0 : Math.min(list[index - 1].to + 1, 100);
    const to = level.to < from ? from : level.to;

    return { ...level, from, to };
  });
}

export function canAddAssessmentLevel(levels: AssessmentLevelInput[]) {
  if (levels.length >= MAX_ASSESSMENT_LEVELS) return false;
  const lastLevel = levels.at(-1);
  if (!lastLevel) return false;

  return lastLevel.to - lastLevel.from >= 2;
}

export function addAssessmentLevel(levels: AssessmentLevelInput[]): AssessmentLevelInput[] {
  const lastLevel = levels.at(-1);
  if (!lastLevel || !canAddAssessmentLevel(levels)) return levels;

  const middle = Math.floor((lastLevel.from + lastLevel.to) / 2);
  const updatedLastLevel = { ...lastLevel, to: middle };
  const nextLevel: AssessmentLevelInput = {
    from: middle + 1,
    to: lastLevel.to,
    label: "",
    color: palette.grade[levels.length as keyof Palette["grade"]],
  };

  return [...levels.slice(0, -1), updatedLastLevel, nextLevel];
}

export function removeAssessmentLevel(levels: AssessmentLevelInput[], index: number): AssessmentLevelInput[] {
  if (levels.length <= MIN_ASSESSMENT_LEVELS) return levels;
  if (index < 0 || index >= levels.length) return levels;

  const deletedLevel = levels[index];
  if (!deletedLevel) return levels;

  if (index === levels.length - 1) {
    const previousLevel = levels[index - 1];
    if (!previousLevel) return levels;

    const updatedPreviousLevel = { ...previousLevel, to: deletedLevel.to };
    return [...levels.slice(0, index - 1), updatedPreviousLevel];
  }

  const nextLevel = levels[index + 1];
  if (!nextLevel) return levels.filter((_, currentIndex) => currentIndex !== index);

  const updatedNextLevel = { ...nextLevel, from: deletedLevel.from };
  return [...levels.slice(0, index), updatedNextLevel, ...levels.slice(index + 2)];
}

export const getLevelTitle = (level: AssessmentLevelInput): string => {
  return `${level.label || "-"}: ${level.from} - ${level.to}`;
};
